<template>
  <div class="ds-tooltip-wrapper">
    <slot ref="slotElement"></slot>
    <transition name="fade">
      <div v-if="visible" class="ds-tooltip ds-text-small">
        <p class="ds-font-weight-bold">{{ formattedDate }}</p>
        <p>{{ description }}</p>
      </div>
    </transition>
  </div>
</template>

<script>
import { parseDate } from "@/utils/date";

export default {
  name: "StatusUptimeTooltip",
  props: {
    date: {
      type: String,
      required: false,
    },
    description: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      visible: false,
    };
  },
  computed: {
    formattedDate() {
      return parseDate(this.date, "MMMM do");
    },
  },
  methods: {
    showTooltip(event) {
      this.visible = true;
      this.$nextTick(() => {
        const tooltip = this.$el.querySelector(".ds-tooltip");
        if (!tooltip) return;

        const parent = event.currentTarget;
        const tooltipRect = tooltip.getBoundingClientRect();
        const parentRect = parent.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        // Default: Center the tooltip
        let newLeft = parentRect.width / 2;
        let newTransform = "translateX(-50%)";

        const spaceOnRight = viewportWidth - parentRect.right;
        const spaceOnLeft = parentRect.left;

        // If tooltip **completely overflows right**, shift left
        if (parentRect.right + tooltipRect.width / 2 > viewportWidth) {
          newLeft = parentRect.width - tooltipRect.width; // Shift fully left
          newTransform = "translateX(0)";
        }
        // If tooltip **completely overflows left**, shift right
        else if (parentRect.left - tooltipRect.width / 2 < 0) {
          newLeft = 0; // Shift fully right
          newTransform = "translateX(0)";
        }
        // If there's **some space left or right**, stay centered
        else if (
          spaceOnRight > tooltipRect.width / 2 &&
          spaceOnLeft > tooltipRect.width / 2
        ) {
          newLeft = parentRect.width / 2;
          newTransform = "translateX(-50%)";
        }

        tooltip.style.left = `${newLeft}px`;
        tooltip.style.transform = newTransform;
      });
    },
    hideTooltip() {
      this.visible = false;
    },
  },
  mounted() {
    // Find the first element inside the slot and attach hover events
    this.$nextTick(() => {
      const slotElement = this.$el.querySelector("*"); // Gets the first child inside slot
      if (slotElement) {
        slotElement.addEventListener("mouseover", this.showTooltip);
        slotElement.addEventListener("mouseleave", this.hideTooltip);
      }
    });
  },
  beforeUnmount() {
    // Clean up event listeners to prevent memory leaks
    const slotElement = this.$el.querySelector("*");
    if (slotElement) {
      slotElement.removeEventListener("mouseover", this.showTooltip);
      slotElement.removeEventListener("mouseleave", this.hideTooltip);
    }
  },
};
</script>

<style lang="scss" scoped>
@import "~website-design-system/src/foundation/tokens/variables";

.ds-tooltip-wrapper {
  position: relative;

  &:hover {
    cursor: pointer;
  }
}

.ds-tooltip {
  background-color: $color-black;
  bottom: 125%;
  color: $color-white;
  left: 50%;
  overflow-wrap: break-word;
  padding: $spacing-12;
  position: absolute;
  transform: translateX(-50%);
  white-space: normal;
  width: 164px;
  word-wrap: normal;
  z-index: 10;

  p:first-child {
    margin-bottom: 4px;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
